import { Box, Chip, Divider, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import validator from 'validator';
import { useInput } from '../../../customHook/useInput';
import useTaskFileUploader from '../../../customHook/useTaskFileUpload';
import { TaskService } from '../../../service/task.service';
import zeplinColor from '../../../theme/Color';
import CustomAlert from '../CustomAlert/CustomAlert';
import CustomAlertContain from '../CustomAlert/CustomAlertContain';
import CustomButton from '../CustomButton/CustomButton';
import CustomCard from '../CustomCard/CustomCard';
import CustomDrawer from '../CustomDrawer/CustomDrawer';
import CustomModal from '../CustomModal/CustomModal';
import CustomPlatformSelection from '../CustomPlatformSelection/CustomPlatformSelection';
import style from './CustomAddTask.module.css';
import CustomAddTaskForm from './CustomAddTaskForm/CustomAddTaskForm';

const CustomAddTask = ({
  handleContinueAdd,
  handleModalClose,
  modalOpen,
  setDrawerState,
  drawerState,
  edit,
  setEdit,
  data,
  setAdded,
  setEditDone,
  setTaskAnchorEl,
  hideSourceChip,
}: {
  handleContinueAdd: () => void;
  handleModalClose: () => void;
  modalOpen: boolean;
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
  edit?: boolean;
  setEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  data?: any;
  setAdded?: React.Dispatch<React.SetStateAction<boolean>>;
  setEditDone?: React.Dispatch<React.SetStateAction<boolean>>;
  setTaskAnchorEl?: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  hideSourceChip?: boolean;
}) => {
  const location = useLocation();
  const redirectPath = sessionStorage.getItem('redirect_path') || '';
  let selectedMember: any = {};
  const storedMember = sessionStorage.getItem('selected-member');
  if (storedMember) {
    selectedMember = JSON.parse(storedMember);
  }
  const details = location.pathname?.split('/');
  const Email = sessionStorage.getItem('current-user') || '';
  const sf_token = sessionStorage.getItem('SFToken') || '';

  const [addSubTask, setAddSubTask] = useState(false);
  const [date, setDate] = useState<Dayjs | null>(
    dayjs(data?.dueDate).date() ? dayjs(data?.dueDate) : null,
  );
  const [alert, setAlert] = React.useState('');
  const OnClickSubTask = () => {
    setAddSubTask((prev) => !prev);
  };

  const [taskTypeSelected, setTaskTypeSelected] = useState(
    edit ? data?.recordTypeName : 'A&A Management Task',
  );
  const getAttachmentsArray = (attachments?: any): (File | string)[] => {
    if (!attachments) return [];

    if (typeof attachments === 'object' && !Array.isArray(attachments) && 'added' in attachments) {
      return Array.isArray(attachments.added) ? [...attachments.added] : [];
    }

    if (Array.isArray(attachments)) {
      return [...attachments];
    }

    if (typeof attachments === 'string') {
      return [attachments];
    }

    return [];
  };

  const [selectedFiles, setSelectedFiles] = useState<(File | string)[]>(
    getAttachmentsArray(data?.attachments),
  );
  const [attachments, setAttachments] = useState<{
    removed: string[];
    added: string[];
  }>({
    removed: [],
    added: [],
  });
  const attachmentsRef = useRef(attachments);
  const { uploadTaskFiles } = useTaskFileUploader();

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    if (edit && data?.attachments) {
      const extractedAttachments = getAttachmentsArray(data.attachments);
      setSelectedFiles(extractedAttachments);
      setAttachments({
        removed: [],
        added: [],
      });
    }
  }, [data?.attachments, edit]);

  const onDrop = (acceptedFiles: File[]) => {
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  };

  const handleDeleteFiles = (index: number) => {
    const fileToRemove = selectedFiles[index];
    const isExistingFile = typeof fileToRemove === 'string';

    setSelectedFiles(selectedFiles.filter((_, ind) => ind !== index));

    if (isExistingFile && edit) {
      setAttachments((prev) => ({
        ...prev,
        removed: [...prev.removed, fileToRemove],
      }));
    } else {
      const fileIndex = selectedFiles.slice(0, index).filter((f) => f instanceof File).length;
      setAttachments((prev) => ({
        added: prev.added.filter((_, ind) => ind !== fileIndex),
        removed: [...prev.removed],
      }));
    }
  };

  const {
    handleChange: handleCommentChange,
    handleBlur: handleCommentBlur,
    clearInput: clearComment,
    value: comment,
    invalid: invalidComment,
    didChange: didCommentChange,
  } = useInput(data?.description ? data?.description : '', () => true);
  const {
    handleChange: handleEmailChange,
    handleBlur: handleEmailBlur,
    clearInput: clearEmail,
    value: email,
    invalid: invalidEmail,
    didChange: didEmailChange,
  } = useInput(data?.email ? data?.email : '', validator.isEmail);
  const {
    handleChange: handleLinkChange,
    handleBlur: handleLinkBlur,
    clearInput: clearLink,
    value: link,
    invalid: invalidLink,
    didChange: didLinkChange,
  } = useInput(data?.link ? data?.link : '', () => true);
  const {
    handleChange: handlePayeeChange,
    handleBlur: handlePayeeBlur,
    clearInput: clearPayee,
    value: payee,
    invalid: invalidPayee,
    didChange: didPayeeChange,
  } = useInput(data?.payee ? data?.payee : '', () => true);
  const {
    handleChange: handleQTYChange,
    handleBlur: handleQTYBlur,
    clearInput: clearQTY,
    value: qTY,
    invalid: invalidQTY,
    didChange: didQTYChange,
  } = useInput(data?.qty ? data?.qty : '', () => true);
  const {
    handleChange: handleRateChange,
    handleBlur: handleRateBlur,
    clearInput: clearRate,
    value: rate,
    invalid: invalidRate,
    didChange: didRateChange,
  } = useInput(data?.rate ? data?.rate : '', () => true);
  const {
    handleChange: handleSubjectChange,
    handleBlur: handleSubjectBlur,
    clearInput: clearSubject,
    value: subject,
    invalid: invalidSubject,
    didChange: didSubjectChange,
  } = useInput(data ? data?.subject : '', () => true);

  const [subTask, setSubtask] = useState<
    {
      subject: string;
      comment: string;
      link: string;
    }[]
  >([]);
  const [platformSelected, setPlatformSelected] = useState(
    (sf_token !== 'false' && redirectPath) || (details[2] !== 'huddle' && details[1] === 'clients')
      ? 'Salesforce'
      : 'Huddle',
  );
  const [formLoading, setFormLoading] = useState(false);

  const addTask = async () => {
    try {
      setFormLoading(true);

      const filesToUpload = selectedFiles.filter((f): f is File => f instanceof File);
      let uploadedUrls: string[] = [];

      if (filesToUpload.length > 0) {
        try {
          uploadedUrls = await uploadTaskFiles(filesToUpload, Email);

          if (uploadedUrls.length !== filesToUpload.length) {
            setAlert(
              `Warning: Some files failed to upload. Only ${uploadedUrls.length} out of ${filesToUpload.length} files uploaded.`,
            );
          }
        } catch (uploadError) {
          setAlert('Failed to upload files. Please try again.');
          setFormLoading(false);
          return;
        }
      }

      const existingUrls = selectedFiles.filter((f): f is string => typeof f === 'string');
      const allAttachmentUrls = [...existingUrls, ...uploadedUrls];

      const currentAttachments = attachmentsRef.current;
      const removedUrls = edit ? currentAttachments?.removed || [] : [];

      const formatAssignee = () => {
        if (!selectedItems?.assignTo || selectedItems.assignTo.length === 0) {
          return undefined;
        }

        const validAssignees = selectedItems.assignTo.filter((assignee: any) => assignee?.Id);

        if (validAssignees.length === 0) {
          return undefined;
        }

        const result = validAssignees.map((selectedAssignee: any) => {
          const fullAssignee: any = Array.isArray(assign)
            ? assign.find((a: any) => a?.Id === selectedAssignee.Id)
            : null;

          const assigneeObj = fullAssignee || selectedAssignee;

          return {
            Name: assigneeObj?.Name || selectedAssignee?.Name || '',
            Id: assigneeObj?.Id || selectedAssignee?.Id || '',
            Email: assigneeObj?.Email || selectedAssignee?.Email || '',
          };
        });
        return result;
      };

      const addProps: any = {
        subject: subject,
        recordTypeName: taskTypeSelected,
        ...(date && date.isValid() && { dueDate: date.toISOString() }),
        ...(comment && { description: comment }),
        ...(selectedItems.related[0]?.Id && {
          relatedTo: {
            Id: selectedItems.related[0].Id,
            Name: selectedItems.related[0].Name || '',
          },
        }),
        ...(selectedItems.relatedType[0] && {
          relatedType:
            selectedItems.relatedType[0].charAt(0).toUpperCase() +
            selectedItems.relatedType[0].slice(1),
        }),
        ...(taskTypeSelected === 'Payment Task' && {
          ...(selectedItems?.payee[0]?.Id && { payeeId: selectedItems?.payee[0]?.Id }),
          ...(rate && { rate: +rate }),
          ...(qTY && { qty: +qTY }),
          ...(selectedItems?.terms[0] !== 'None' && { terms: selectedItems?.terms[0] }),
          ...(email && { email: email }),
        }),
        status: Array.isArray(selectedItems.status)
          ? selectedItems.status[0]
          : selectedItems.status,
        priority: Array.isArray(selectedItems.priority)
          ? selectedItems.priority[0]
          : selectedItems.priority,
        ...(selectedItems.name[0]?.Id && {
          name: {
            Id: selectedItems.name[0].Id,
            Name: selectedItems.name[0].Name || '',
          },
        }),
        ...(selectedItems.nameType[0] && {
          nameType:
            selectedItems.nameType[0].charAt(0).toUpperCase() + selectedItems.nameType[0].slice(1),
        }),
        link,
      };

      if (allAttachmentUrls.length > 0) {
        addProps.attachments = {
          added: allAttachmentUrls,
          removed: [],
        };
      }

      const formatAssigneeForUpdate = () => {
        if (!selectedItems?.assignTo || selectedItems.assignTo.length === 0) {
          return undefined;
        }

        const validAssignees = selectedItems.assignTo.filter((assignee: any) => assignee?.Id);

        if (validAssignees.length === 0) {
          return undefined;
        }

        const result = validAssignees.map((selectedAssignee: any) => {
          const fullAssignee: any = Array.isArray(assign)
            ? assign.find((a: any) => a?.Id === selectedAssignee.Id)
            : null;

          const assigneeObj = fullAssignee || selectedAssignee;

          return {
            Name: assigneeObj?.Name || selectedAssignee?.Name || '',
            Id: assigneeObj?.Id || selectedAssignee?.Id || '',
            Email: assigneeObj?.Email || selectedAssignee?.Email || '',
          };
        });
        return result;
      };

      const updateData: any = {
        subject: subject,
        ...(comment && { description: comment }),
        ...(taskTypeSelected && { recordTypeName: taskTypeSelected }),
        status: Array.isArray(selectedItems.status)
          ? selectedItems.status[0]
          : selectedItems.status,
        priority: Array.isArray(selectedItems.priority)
          ? selectedItems.priority[0]
          : selectedItems.priority,
        ...(date && date.isValid() && { dueDate: date.toISOString() }),
        ...(selectedItems.related[0]?.Id && {
          relatedTo: {
            Id: selectedItems.related[0].Id,
            Name: selectedItems.related[0].Name || '',
          },
        }),
        ...(selectedItems.relatedType[0] && {
          relatedType:
            selectedItems.relatedType[0].charAt(0).toUpperCase() +
            selectedItems.relatedType[0].slice(1),
        }),
        ...(selectedItems.name[0]?.Id && {
          name: {
            Id: selectedItems.name[0].Id,
            Name: selectedItems.name[0].Name || '',
          },
        }),
        ...(selectedItems.nameType[0] && {
          nameType:
            selectedItems.nameType[0].charAt(0).toUpperCase() + selectedItems.nameType[0].slice(1),
        }),
        ...(taskTypeSelected === 'Payment Task' && {
          ...(selectedItems?.payee[0]?.Id && { payeeId: selectedItems?.payee[0]?.Id }),
          ...(rate && { rate: +rate }),
          ...(qTY && { qty: +qTY }),
          ...(selectedItems?.terms[0] !== 'None' && { terms: selectedItems?.terms[0] }),
          ...(email && { email: email }),
        }),
        link,
      };

      if (edit && (allAttachmentUrls.length > 0 || removedUrls.length > 0)) {
        updateData.attachments = {
          added: allAttachmentUrls,
          removed: removedUrls,
        };
      }

      const formattedAssigneeForUpdate = formatAssigneeForUpdate();
      if (formattedAssigneeForUpdate) {
        updateData.assignedTo = formattedAssigneeForUpdate;
      }

      const formattedAssignee = formatAssignee();
      if (formattedAssignee) {
        addProps.assignedTo = formattedAssignee;
      }

      if (!edit && allAttachmentUrls.length > 0) {
        addProps.attachments = {
          added: allAttachmentUrls,
          removed: [],
        };
      }

      const finalPayload = edit ? updateData : addProps;

      if (!edit && allAttachmentUrls.length > 0 && !finalPayload.attachments) {
        addProps.attachments = {
          added: allAttachmentUrls,
          removed: [],
        };
      }
      edit
        ? await TaskService.updateTask(data?.id, updateData)
        : await TaskService.createTask(addProps);

      sessionStorage.removeItem('sf_redirect');
      sessionStorage.removeItem('sf_edit');
      sessionStorage.removeItem('sf_delete');
      clearComment();
      clearLink();
      clearSubject();
      setDrawerState(false);
      if (setAdded) setAdded(true);
      if (setEdit) setEdit(false);
      if (setEditDone) setEditDone(true);
    } catch (err: any) {
      setAlert(err?.response?.data?.error || 'Failed to create task');
    } finally {
      setFormLoading(false);
    }
  };

  const [assign, setAssign] = useState([{ Name: '', Id: '' }]);
  const [Priority, setPriority] = useState<string[]>(['Medium', 'Low', 'High']);
  const [Status, setStatus] = useState<string[]>(['To Do', 'In Progress', 'Done']);

  const onClose = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    sessionStorage.removeItem('redirect_path');
    sessionStorage.removeItem('sf_redirect');
    sessionStorage.removeItem('sf_edit');
    sessionStorage.removeItem('sf_delete');
    clearComment();
    clearLink();
    clearSubject();
    clearEmail();
    clearQTY();
    clearRate();
    clearPayee();
    setDrawerState(open);
    setSelectedFiles([]);
    setSelectedItems({
      priority: '',
      status: '',
      related: [],
      relatedType: [],
      assignTo: [],
      name: [],
      nameType: [],
      payee: [],
      dueDate: '',
      terms: [],
    });

    setSubtask([]);
    setEdit && setEdit(false);
  };

  const [selectedClient, setSelectedClient] = React.useState<any>();
  const [recordType, setRecordType] = useState([
    'A&A Management Task',
    'Brick Media Task',
    'Payment Task',
  ]);

  const [selectedItems, setSelectedItems] = useState<{
    priority: string;
    status: string;
    related: { Name: string; Id: string }[];
    assignTo: Array<{ Name: string; Id: string; Email?: string }>;
    name: { Name: string; Id: string }[];
    payee: { Name: string; Id: string }[];
    nameType: string[];
    relatedType: string[];
    dueDate: string;
    terms: string[];
  }>({
    priority: data ? data?.priority : '',
    status:
      data?.status === 'Completed'
        ? 'Done'
        : data?.status === 'Not Started'
          ? 'To Do'
          : data?.status
            ? data?.status
            : '',
    related: data?.relatedTo?.Id
      ? [data?.relatedTo]
      : details[1] === 'clients'
        ? [{ Name: selectedClient?.accountName, Id: selectedClient?.accountId }]
        : [{ Name: '', Id: '' }],
    relatedType: data?.relatedType
      ? [data.relatedType.toLocaleLowerCase()]
      : details[1] === 'clients'
        ? ['Account']
        : [''],
    name: data?.nameId ? [data?.nameId] : [{ Name: '', Id: '' }],
    payee: data?.payee?.Id
      ? [{ Name: data?.payee?.Name, Id: data?.payee.Id }]
      : [{ Name: '', Id: '' }],
    nameType: data?.nameType ? [data.nameType.toLocaleLowerCase()] : [''],
    assignTo:
      details[1] === 'team'
        ? [{ Name: selectedMember?.name, Id: selectedMember?.id }]
        : data?.assignedTo
          ? Array.isArray(data.assignedTo)
            ? data.assignedTo.filter((assignee: any) => assignee?.Id && assignee?.Name)
            : data.assignedTo?.Id && data.assignedTo?.Name
              ? [data.assignedTo]
              : []
          : [],
    dueDate: data ? data?.dueDate : '',
    terms: data?.terms ? [data?.terms] : [''],
  });

  const [relatedTo, setRelatedTo] = useState({
    account: [{ Name: '', Id: '' }],
    deal: [{ Name: '', Id: '' }],
  });
  const [name, setName] = useState({
    contact: [{ Name: '', Id: '' }],
    lead: [{ Name: '', Id: '' }],
  });

  const getDropDown = async () => {
    try {
      const res = await TaskService.getTaskDropdowns();
      setRelatedTo(res.relatedTo);
      setAssign(res.assignTo);
      setName({ contact: res.nameContact, lead: res.nameLead });
    } catch (err) {
    } finally {
    }
  };

  const getFilterValues = async () => {
    try {
      const res = await TaskService.getClientFilterValues();
      if (res?.task_filter_values?.priority && res.task_filter_values.priority.length > 0) {
        setPriority(res.task_filter_values.priority);
      }
      if (res?.task_filter_values?.status && res.task_filter_values.status.length > 0) {
        setStatus(res.task_filter_values.status);
      }
    } catch (err) {}
  };

  const handleSelection = (item: any, type: string) => {
    const key = type as keyof typeof selectedItems;
    if (Array.isArray(item)) {
      setSelectedItems((prev) => ({ ...prev, [key]: [...item] }));
    } else if (item?.target?.value) {
      const {
        target: { value },
      } = item;

      setSelectedItems((prev) => ({
        ...prev,
        [key]: typeof value === 'string' ? value.split(',') : value,
      }));
    } else {
      if (key === 'assignTo' && item?.Id) {
        setSelectedItems((prev) => {
          const currentAssignees = prev.assignTo || [];
          const exists = currentAssignees.some((assignee: any) => assignee?.Id === item.Id);
          if (exists) {
            return {
              ...prev,
              [key]: currentAssignees.filter((assignee: any) => assignee?.Id !== item.Id),
            };
          } else {
            return {
              ...prev,
              [key]: [...currentAssignees, item],
            };
          }
        });
      } else {
        setSelectedItems((prev) => ({
          ...prev,
          [key]: [item],
        }));
      }
    }
  };

  const handleDeleteItems = (item: any, type: string) => {
    const key = type as keyof typeof selectedItems;

    setSelectedItems({
      ...selectedItems,
      [key]: [...selectedItems.assignTo?.filter((val: any) => val !== item)],
    });
  };

  const handleTaskType = (item: any) => {
    setTaskTypeSelected(item.target.value);
  };
  const handleClose = () => {
    setAlert('');
  };
  useEffect(() => {
    getDropDown();
  }, [edit]);

  useEffect(() => {
    getFilterValues();
  }, []);

  return (
    <Box>
      <CustomModal handleClose={() => {}} open={modalOpen}>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: { xs: '16px', xl: '24px' },
              backgroundColor: zeplinColor.Neutral1,

              width: { xs: '280px', xl: '312px' },
              boxShadow: 'rgba(0, 0, 0, 0.15)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '14px', xl: '24px' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                    Create Task
                  </Typography>
                  <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                    {details[1] !== 'clients'
                      ? 'Select where you want to create this task and choose its type.'
                      : ''}
                  </Typography>
                </Box>
                <img
                  src={'/assets/cross.svg'}
                  alt='logo'
                  className={style.icon}
                  onClick={handleModalClose}
                  style={{ cursor: 'pointer' }}
                />
              </Box>
              {setTaskAnchorEl && details[1] !== 'clients' && (
                <CustomPlatformSelection
                  sf_token={sf_token}
                  setPlatformSelected={setPlatformSelected}
                  platformSelected={platformSelected}
                  setAnchorEl={setTaskAnchorEl}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                  Select Task Type
                </Typography>
                <RadioGroup
                  aria-labelledby='demo-controlled-radio-buttons-group'
                  name='controlled-radio-buttons-group'
                  value={taskTypeSelected}
                  onChange={handleTaskType}
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': {
                      color: zeplinColor.BasicBlack,
                    },
                    padding: '0px',
                  }}
                >
                  {recordType.map((name) => (
                    <FormControlLabel
                      value={name}
                      control={
                        <Radio
                          sx={{
                            color: zeplinColor.BasicBlack,
                            '&.Mui-checked': {
                              color: zeplinColor.BasicBlack,
                            },
                          }}
                        />
                      }
                      label={name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '& .MuiFormControlLabel-label': {
                          fontSize: { xs: '14px', xl: '16px' },
                        },
                        '& .MuiButtonBase-root': {
                          fontSize: { xs: '14px', xl: '16px' },
                          color: zeplinColor.BasicBlack,
                        },
                        padding: '0px',
                      }}
                    />
                  ))}
                </RadioGroup>
              </Box>
              <CustomButton
                disabled={recordType[0] ? false : true}
                onClick={handleContinueAdd}
                variant='contained'
                color={'primary'}
                style={{
                  width: '100%',
                  borderRadius: '14px',
                  height: 48,
                  padding: '14px 16px',
                  '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                }}
                textStyle={{ color: zeplinColor.Neutral1, fontSize: { xs: '14px', xl: '16px' } }}
                text={'Continue'}
              />
            </Box>
          </CustomCard>
        </Box>
      </CustomModal>

      {drawerState && (
        <CustomDrawer state={drawerState} onClose={onClose(false)}>
          <Box
            sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'scroll' }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                backgroundColor: zeplinColor.Neutral1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '40px 24px 24px 24px',
                }}
              >
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {addSubTask ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                      }}
                    >
                      <img
                        src={'/assets/arrow-left-blue.svg'}
                        className={style.icon}
                        alt='close'
                        style={{ cursor: 'pointer' }}
                        onClick={OnClickSubTask}
                      />
                      <Typography variant='responsiveHeading2'>Subtask</Typography>
                    </Box>
                  ) : edit ? (
                    <Typography variant='responsiveHeading2'>Edit Task</Typography>
                  ) : (
                    <Typography variant='responsiveHeading2'>
                      New Task: {taskTypeSelected}
                    </Typography>
                  )}
                  {!hideSourceChip && (
                    <Chip
                      label={
                        <Typography
                          variant='label1'
                          color={
                            details[1] === 'huddle' || platformSelected === 'Huddle'
                              ? zeplinColor.BasicBlack
                              : zeplinColor.Neutral1
                          }
                          sx={{
                            textAlign: 'center',
                            display: 'block',
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {details[1] === 'huddle' ? 'Huddle' : platformSelected}
                        </Typography>
                      }
                      sx={{
                        backgroundColor:
                          details[1] === 'huddle' || platformSelected === 'Huddle'
                            ? zeplinColor.Brand2
                            : zeplinColor.Blue,
                        padding: '5px 12px 5px 12px',
                        borderRadius: '50px',
                        height: '24px',
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          maxWidth: '120px',
                        },
                      }}
                    />
                  )}
                </Box>
                {!addSubTask && (
                  <img
                    src={'/assets/close-icon.svg'}
                    className={style.icon}
                    alt='close'
                    style={{ cursor: 'pointer' }}
                    onClick={
                      edit
                        ? () => {
                            setEdit && setEdit(false);
                            setDrawerState(false);
                            sessionStorage.removeItem('redirect_path');
                          }
                        : onClose(false)
                    }
                  />
                )}
              </Box>

              <Divider
                component='div'
                sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
              />
            </Box>

            <CustomAddTaskForm
              subtask={addSubTask}
              OnClickSubtask={OnClickSubTask}
              payee={payee}
              handlePayeeChange={handlePayeeChange}
              handlePayeeBlur={handlePayeeBlur}
              invalidPayee={invalidPayee}
              didPayeeChange={didPayeeChange}
              rate={rate}
              didRateChange={didRateChange}
              handleRateChange={handleRateChange}
              invalidRate={invalidRate}
              handleRateBlur={handleRateBlur}
              handleQTYChange={handleQTYChange}
              handleQTYBlur={handleQTYBlur}
              qTY={qTY}
              didQTYChange={didQTYChange}
              invalidQTY={invalidQTY}
              handleCommentChange={handleCommentChange}
              handleCommentBlur={handleCommentBlur}
              comment={comment}
              invalidComment={invalidComment}
              didCommentChange={didCommentChange}
              handleLinkChange={handleLinkChange}
              handleLinkBlur={handleLinkBlur}
              link={link}
              invalidLink={invalidLink}
              didLinkChange={didLinkChange}
              handleSubjectChange={handleSubjectChange}
              handleSubjectBlur={handleSubjectBlur}
              subject={subject}
              invalidSubject={invalidSubject}
              didSubjectChange={didSubjectChange}
              error={''}
              Priority={Priority}
              Status={Status}
              selectedItems={selectedItems}
              handleSelection={handleSelection}
              onDrop={onDrop}
              selectedFiles={selectedFiles}
              add={() => {
                addTask();
              }}
              deleteFile={handleDeleteFiles}
              selectedType={taskTypeSelected}
              subTask={subTask}
              date={date}
              setDate={setDate}
              handleDeleteItems={handleDeleteItems}
              Assign={assign}
              relatedTo={relatedTo}
              name={name}
              edit={edit ? true : false}
              key={assign[0].Id}
              email={email}
              handleEmailChange={handleEmailChange}
              handleEmailBlur={handleEmailBlur}
              didEmailChange={didEmailChange}
              invalidEmail={invalidEmail}
              formLoading={formLoading}
              setFormLoading={setFormLoading}
            />
          </Box>
        </CustomDrawer>
      )}

      <CustomAlert
        open={alert ? true : false}
        handleClose={handleClose}
        message={
          <CustomAlertContain
            alert={alert}
            alertIcon={<img src='/assets/error.svg' width={24} height={24} alt='error sign' />}
            error={alert ? true : false}
          />
        }
        error={alert ? true : false}
      />
    </Box>
  );
};

export default CustomAddTask;
